import React, { useEffect, useState, useMemo } from 'react';
import { Tooltip, Input, Select, Card, Space } from 'antd';
import { ReloadOutlined, LeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import StandardTable from '../../../components/common/StandardTable';
import { useGetMatGapMaster } from '../../../hooks/genericHooks';
import Button from '../../../components/common/Button';

// Using Select options API instead of Option children for better search control

const MatGapMasterList: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, refetch } = useGetMatGapMaster();
  const matGapArray = data?.data ?? [];
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Filter for "Malzeme kısa metni"
  const [shortTextSearch, setShortTextSearch] = useState('');
  const [debouncedShortText, setDebouncedShortText] = useState('');
  const [malGruplari, setMalGruplari] = useState<any[]>([]);
  const [malGrupFilter, setMalGrupFilter] = useState<string>('');
  const [hrcMalGruplari, setHrcMalGruplari] = useState<any[]>([]);
  const [hrcGrupFilter, setHrcGrupFilter] = useState<string>('');
  const [pageSize, setPageSize] = useState<number>(20);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalRecords, setTotalRecords] = useState<number>(0);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  // Debounce for short text filter
  useEffect(() => {
    const t = setTimeout(() => setDebouncedShortText(shortTextSearch), 300);
    return () => clearTimeout(t);
  }, [shortTextSearch]);

  useEffect(() => {
    fetchMalGruplari();
  }, [matGapArray, malGrupFilter, hrcGrupFilter, debouncedSearch]);

  const fetchMalGruplari = async () => {
    try {
      const normalize = (s: any) => (s == null ? '' : String(s)).trim();
      const matchesMal = (row: any) => !malGrupFilter || normalize(row['Mal grubu tanımı']).toLowerCase() === normalize(malGrupFilter).toLowerCase();
      const matchesHrc = (row: any) => !hrcGrupFilter || normalize(row['Hrc.mal grubu tanımı']).toLowerCase() === normalize(hrcGrupFilter).toLowerCase();
      const searchVal = (debouncedSearch || '').toLowerCase();
      const phraseMatches = Array.from(searchVal.matchAll(/"([^"]+)"/g)).map(m => m[1].trim()).filter(Boolean);
      const remaining = searchVal.replace(/"[^"]+"/g, ' ').trim();
      const tokens = remaining.split(/\s+/).filter(Boolean);
      const matchesSearch = (row: any) => {
        if (tokens.length === 0 && phraseMatches.length === 0) return true;
        try {
          const hay = Object.entries(row)
            .filter(([k]) => k !== 'Mal grubu tanımı' && k !== 'Hrc.mal grubu tanımı')
            .map(([, v]) => (v == null ? '' : String(v)))
            .join(' ')
            .toLowerCase();
          if (phraseMatches.length > 0) {
            return phraseMatches.every(p => hay.includes(p));
          }
          return tokens.every(t => hay.includes(t));
        } catch {
          return false;
        }
      };

      // Mal grubu tanımı options depend only on current HRC filter
      const map = new Map<string, string>(); // key: lowercased, value: first-seen trimmed label
      (matGapArray || [])
        .filter((r: any) => matchesHrc(r))
        .filter((r: any) => matchesSearch(r))
        .forEach((r: any) => {
          const raw = normalize(r['Mal grubu tanımı']);
          if (!raw) return;
          const key = raw.toLowerCase();
          if (!map.has(key)) map.set(key, raw);
        });
      const uniqueSorted = Array.from(map.values()).sort((a: string, b: string) => a.localeCompare(b));
      setMalGruplari(uniqueSorted.map((g: string) => ({ value: g, label: g })));

      // Hrc.mal grubu tanımı options depend only on current Mal group filter
      const mapHrc = new Map<string, string>();
      (matGapArray || [])
        .filter((r: any) => matchesMal(r))
        .filter((r: any) => matchesSearch(r))
        .forEach((r: any) => {
          const raw = normalize(r['Hrc.mal grubu tanımı']);
          if (!raw) return;
          const key = raw.toLowerCase();
          if (!mapHrc.has(key)) mapHrc.set(key, raw);
        });
      const uniqueHrcSorted = Array.from(mapHrc.values()).sort((a: string, b: string) => a.localeCompare(b));
      setHrcMalGruplari(uniqueHrcSorted.map((g: string) => ({ value: g, label: g })));
    } catch (err) {
      setMalGruplari([]);
      setHrcMalGruplari([]);
    }
  };

  const filteredData = useMemo(() => {
    const searchVal = (debouncedSearch || '').toLowerCase();
    const phraseMatches = Array.from(searchVal.matchAll(/"([^"]+)"/g)).map(m => m[1].trim()).filter(Boolean);
    const remaining = searchVal.replace(/"[^"]+"/g, ' ').trim();
    const tokens = remaining.split(/\s+/).filter(Boolean);
    // Build multi-word AND filter (order-insensitive) for 'Malzeme kısa metni'
    const shortVal = (debouncedShortText || '').toLowerCase().trim();
    const shortTokens = shortVal.split(/\s+/).filter(Boolean);
    const matchesSearch = (row: any) => {
      if (tokens.length === 0 && phraseMatches.length === 0) return true;
      // Stringify all non-null field values and do a case-insensitive contains search (AND across tokens)
      try {
        const hay = Object.entries(row)
          .filter(([k]) => k !== 'Mal grubu tanımı' && k !== 'Hrc.mal grubu tanımı')
          .map(([, v]) => (v == null ? '' : String(v)))
          .join(' ')
          .toLowerCase();
        if (phraseMatches.length > 0) {
          // If there are quoted phrases, use exact phrase matching (AND across phrases)
          return phraseMatches.every(p => hay.includes(p));
        }
        // Otherwise fall back to token AND matching
        return tokens.every(t => hay.includes(t));
      } catch {
        return false;
      }
    };
    const matchesShortText = (row: any) => {
      if (shortTokens.length === 0) return true;
      try {
        const field = (row['Malzeme kısa metni'] == null ? '' : String(row['Malzeme kısa metni'])).toLowerCase();
        return shortTokens.every(t => field.includes(t));
      } catch {
        return false;
      }
    };
    const normalize = (s: any) => (s == null ? '' : String(s)).trim().toLowerCase();
    const matchesGroup = (row: any) => {
      const okMal = malGrupFilter ? normalize(row['Mal grubu tanımı']) === normalize(malGrupFilter) : true;
      const okHrc = hrcGrupFilter ? normalize(row['Hrc.mal grubu tanımı']) === normalize(hrcGrupFilter) : true;
      return okMal && okHrc;
    };
    return (matGapArray || []).filter(row => matchesSearch(row) && matchesShortText(row) && matchesGroup(row));
  }, [matGapArray, debouncedSearch, debouncedShortText, malGrupFilter, hrcGrupFilter]);

  const tooltipProps = useMemo(() => ({
    placement: 'topLeft' as const,
    overlayStyle: { zIndex: 2000, maxWidth: 600, wordBreak: 'break-word' as const },
    autoAdjustOverflow: true,
    mouseEnterDelay: 0.1,
  }), []);

  const columns = useMemo(() => [
    { title: 'Üretim yeri', dataIndex: 'Üretim yeri', key: 'Üretim yeri', width: 72, ellipsis: { showTitle: false },
      render: (text: string) => (
        <Tooltip {...tooltipProps} title={text}>
          <span style={{ display: 'inline-block', width: 72, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{text}</span>
        </Tooltip>
      )
    },
    { title: 'Malzeme', dataIndex: 'Malzeme', key: 'Malzeme', width: 100, ellipsis: { showTitle: false },
      render: (text: string) => (
        <Tooltip {...tooltipProps} title={text}>
          <span style={{ display: 'inline-block', width: '100%' }}>{text}</span>
        </Tooltip>
      )
    },
    { title: 'Malzeme kısa metni', dataIndex: 'Malzeme kısa metni', key: 'Malzeme kısa metni', width: 360,
      render: (text: string) => (
        <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{text}</span>
      )
    },
    { title: 'Birim', dataIndex: 'Temel ölçü birimi', key: 'Birim', width: 64, ellipsis: { showTitle: false },
      render: (text: string) => (
        <Tooltip {...tooltipProps} title={text}>
          <span style={{ display: 'inline-block', width: 64, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{text}</span>
        </Tooltip>
      )
    },
    { title: 'Uzun Tanım', dataIndex: 'Malzeme uzun tanımı', key: 'Uzun Tanım', width: 120, ellipsis: { showTitle: false },
      render: (text: string) => (
        <Tooltip {...tooltipProps} title={text}>
          <span style={{ display: 'inline-block', width: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{text}</span>
        </Tooltip>
      )
    },
    { title: 'Mal grubu', dataIndex: 'Mal grubu', key: 'Mal grubu', width: 100, ellipsis: { showTitle: false },
      render: (text: string) => (
        <Tooltip {...tooltipProps} title={text}>
          <span style={{ display: 'inline-block', width: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{text}</span>
        </Tooltip>
      )
    },
    { title: 'Mal grubu tanımı', dataIndex: 'Mal grubu tanımı', key: 'Mal grubu tanımı', width: 180, ellipsis: { showTitle: false },
      render: (_: string, record: any) => (
        <Tooltip {...tooltipProps} title={record["Mal grubu tanımı"] || '(boş)'} overlayStyle={{ maxWidth: 600 }}>
          <span style={{ display: 'inline-block', width: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{record["Mal grubu tanımı"]}</span>
        </Tooltip>
      )
    },
    { title: 'Hiyerarşi', dataIndex: 'Ürün hiyerarşisi', key: 'Hiyerarşi', width: 120, ellipsis: { showTitle: false },
      render: (text: string) => (
        <Tooltip {...tooltipProps} title={text}>
          <span style={{ display: 'inline-block', width: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{text}</span>
        </Tooltip>
      )
    },
    { title: 'Hiyerarşi Tanımı', dataIndex: 'Hiyerarşi Tanımı', key: 'Hiyerarşi Tanımı', width: 180, ellipsis: { showTitle: false },
      render: (text: string) => (
        <Tooltip {...tooltipProps} title={text}>
          <span style={{ display: 'inline-block', width: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{text}</span>
        </Tooltip>
      )
    },
    { title: 'Hrc. Mal Gr.', dataIndex: 'Harici mal grubu', key: 'Hrc. Mal Gr.', width: 120, ellipsis: { showTitle: false },
      render: (text: string) => (
        <Tooltip {...tooltipProps} title={text}>
          <span style={{ display: 'inline-block', width: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{text}</span>
        </Tooltip>
      )
    },
    { title: 'Hrc.mal grubu tanımı', dataIndex: 'Hrc.mal grubu tanımı', key: 'Hrc.mal grubu tanımı', width: 180, ellipsis: { showTitle: false },
      render: (_: string, record: any) => (
        <Tooltip {...tooltipProps} title={record["Hrc.mal grubu tanımı"] || '(boş)'} overlayStyle={{ maxWidth: 600 }}>
          <span style={{ display: 'inline-block', width: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{record["Hrc.mal grubu tanımı"]}</span>
        </Tooltip>
      )
    }
  ], [tooltipProps]);

  const handleTableChange = (pagination: any) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setCurrentPage(1);
  };

  const handleFilterChange = (value: string) => {
    setMalGrupFilter(value || '');
    setCurrentPage(1);
  };
  const handleHrcFilterChange = (value: string) => {
    setHrcGrupFilter(value || '');
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setMalGrupFilter('');
    setHrcGrupFilter('');
    setSearch('');
    setCurrentPage(1);
  };

  return (
    <Card title={(<span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Button icon={<LeftOutlined />} type="text" aria-label="Back" onClick={() => navigate(-1)} /><span style={{ fontSize: 24, fontWeight: 600 }}>Gap Material List</span></span>)} style={{ margin: 24 }}>
      <Space direction="vertical" style={{ width: '100%' }}>
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
          <Button icon={<ReloadOutlined />} onClick={handleResetFilters}>
            Reset Filters
          </Button>
          <Select
            allowClear
            showSearch
            placeholder="Mal Grubu Tanımı Filtrele"
            style={{ width: 180 }}
            value={malGrupFilter || undefined}
            onChange={handleFilterChange}
            options={malGruplari}
            filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
          />
          <Select
            allowClear
            showSearch
            placeholder="Hrc. Mal Grubu Tanımı Filtrele"
            style={{ width: 220 }}
            value={hrcGrupFilter || undefined}
            onChange={handleHrcFilterChange}
            options={hrcMalGruplari}
            filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
          />
          <Input.Search
            placeholder="Malzeme, açıklama veya grup ara..."
            allowClear
            onChange={e => handleSearchChange(e.target.value)}
            style={{ width: 260 }}
          />
          <Input
            placeholder="Malzeme kısa metni ara..."
            allowClear
            value={shortTextSearch}
            onChange={e => setShortTextSearch(e.target.value)}
            style={{ width: 260 }}
          />
        </div>
        <StandardTable
          columns={columns as any}
          dataSource={filteredData}
          rowKey="Malzeme"
          loading={isLoading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: matGapArray.length,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total: number, range: [number, number]) => `${range[0]}-${range[1]} / ${total} kayıt`,
          }}
          onChange={handleTableChange}
          size="small"
          className="compact-table zebra-table left-align-table"
          rowClassName={(_, index) => (index % 2 === 0 ? 'zebra-row' : '')}
          style={{ padding: '2px' }}
          scroll={{ x: 'max-content' }}
        />
      </Space>
    </Card>
  );
};

export default MatGapMasterList;
